/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useState } from 'react'
import { DownOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { registrationSeasonSubtitle } from '@sportninja/common/features/team-balance/utils/registrationSeasons'
import type { RegistrationSeason } from '@sportninja/common/features/team-balance/utils/registrationSeasons'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import Text from 'src/components/ts-components/Text'
import { SCOPE_CONTROL_HEIGHT } from '../constants/registrationScopeRow'
import { balanceLabel } from '../utils/balanceI18n'
import RegistrationScopeField from './RegistrationScopeField'
import RegistrationSeasonSkeleton from './RegistrationSeasonSkeleton'

interface RegistrationSeasonPickerProps {
  seasons: readonly RegistrationSeason[]
  selectedSeasonId: string | null
  onSelect: (seasonId: string) => void
  /** First load of the seasons the active sub-tab needs. */
  isLoading: boolean
}

/**
 * The Registration tab's Season picker — styled like the Schedule tab's Season
 * filter (GameListFilters): transparent select, chevron that toggles the
 * dropdown — with the season's dates on a second line, as the roster picker
 * shows them.
 *
 * Jeff, Oct 2 meeting (F5) — labelled "Season" on both tenants: a rep picks the
 * season their team played, whatever the tenant calls the competition.
 */
const RegistrationSeasonPicker: React.FC<RegistrationSeasonPickerProps> = ({
  seasons,
  selectedSeasonId,
  onSelect,
  isLoading,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const label = balanceLabel('common:season')

  if (isLoading && seasons.length === 0) {
    return (
      <RegistrationScopeField label={label}>
        <RegistrationSeasonSkeleton />
      </RegistrationScopeField>
    )
  }

  const options = seasons.map((season) => ({
    value: season.id,
    label: (
      <div css={optionStyle}>
        <span css={ellipsisStyle}>
          <Text variant='paragraphSmall' color={colors.NEUTRAL_0}>
            {season.schedule_name}
          </Text>
        </span>
        <Text variant='paragraphXSmall' color={colors.NEUTRAL_100}>
          {registrationSeasonSubtitle(season)}
        </Text>
      </div>
    ),
  }))

  // SelectInput renders its own label row, so it takes the label itself — a
  // wrapper label would stack on top of its (empty) row and double the gap.
  return (
    <div css={fieldStyle} data-testid='registration-season-picker'>
      <SelectInput
        label={label}
        disabled={false}
        options={options}
        name='registration-season'
        value={selectedSeasonId ?? ''}
        onChange={onSelect}
        fieldErrors={{}}
        customError={null}
        showSearch={false}
        allowClear={false}
        backgroundColor='transparent'
        open={isOpen}
        onDropdownVisibleChange={setIsOpen}
        suffixIcon={
          <DownOutlined
            css={chevronStyle}
            onClick={() => setIsOpen((open) => !open)}
          />
        }
        style={selectStyle}
      />
    </div>
  )
}

export default RegistrationSeasonPicker

// ---- styled ----
// The same column + 8px label gap RegistrationScopeField gives the Section field.
const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 300px;
  max-width: 100%;
  min-width: 0;
`

// Two lines (name + dates) need more than the 40px default; the Section
// switcher takes the same height so the row lines up.
const selectStyle = css`
  width: 100%;
  height: ${SCOPE_CONTROL_HEIGHT}px !important;

  .ant-select-selection-item {
    display: flex;
    align-items: center;
    min-width: 0;
  }
`

const optionStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const ellipsisStyle = css`
  display: block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const chevronStyle = css`
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
`
